import { GoogleSignInButton } from '@/components/auth/GoogleSignInButton';
import { Field } from '@/components/ui/field';
import { useAuth } from '@/contexts/auth-context';
import { getLoginReturnPath, getMcpReturnTo } from '@/libs/mcp-return-to';
import { Button, Fieldset, Flex, Input, Link, Stack, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import type { AuthError } from './auth-error';
import { authFieldControlProps, authFieldLabelSx } from './auth-field-label';
import { AuthErrorAlert } from './AuthErrorAlert';
import { OrSeparator } from './OrSeparator';

const loginSchema = z.object({
  email: z.string().min(1).email(),
  password: z.string().min(1).min(8),
});

type LoginFormData = z.infer<typeof loginSchema>;

export const LoginForm = ({ oauthError }: { oauthError: AuthError | null }) => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<AuthError | null>(oauthError);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (data: LoginFormData) => {
    setError(null);
    try {
      await login(data);
      navigate(getLoginReturnPath(searchParams));
    } catch (failure: unknown) {
      const message = axios.isAxiosError(failure)
        ? failure.response?.data?.message || t('auth.errors.unexpectedError')
        : t('auth.errors.unexpectedError');
      setError({ message, code: null });
    }
  };

  // Sync OAuth error from parent when mode switches
  useEffect(() => {
    if (oauthError) setError(oauthError);
  }, [oauthError]);

  return (
    <Stack gap={4} width="full">
      {error && <AuthErrorAlert error={error} />}

      <GoogleSignInButton returnTo={getMcpReturnTo(searchParams.get('returnTo'))} />
      <OrSeparator label={t('auth.or')} />

      <Stack as="form" onSubmit={handleSubmit(onSubmit)} gap={0} width="full">
        <Fieldset.Root size="lg" width="100%" colorPalette="primary">
          <Fieldset.Content>
            <VStack gap={3} width="full">
              <Field errorText={errors.email?.message}>
                <label
                  htmlFor="login-email"
                  style={{ ...authFieldLabelSx, display: 'block', marginBottom: '7px' }}
                >
                  {t('authPage.emailLabel')}
                </label>
                <Input
                  id="login-email"
                  {...register('email')}
                  type="email"
                  placeholder={t('authPage.emailPlaceholder')}
                  variant="outline"
                  {...authFieldControlProps}
                  autoComplete="email"
                  size="lg"
                />
              </Field>
              <Field errorText={errors.password?.message}>
                <Flex justify="space-between" align="baseline" mb="7px" w="full">
                  <label htmlFor="login-password" style={authFieldLabelSx}>
                    {t('authPage.passwordLabel')}
                  </label>
                  <Link
                    href="/forgot-password"
                    fontSize="12.5px"
                    fontWeight="medium"
                    color="primary.900"
                    _hover={{ textDecoration: 'underline' }}
                  >
                    {t('auth.forgotPassword.title')}
                  </Link>
                </Flex>
                <Input
                  id="login-password"
                  {...register('password')}
                  type="password"
                  placeholder="••••••••"
                  variant="outline"
                  {...authFieldControlProps}
                  size="lg"
                />
              </Field>
            </VStack>
          </Fieldset.Content>

          <Button
            type="submit"
            colorPalette="primary"
            size="lg"
            width="full"
            loading={isSubmitting}
            loadingText={t('auth.loggingIn')}
            h={{ base: '44px', md: '48px' }}
            bg="primary.900"
            fontSize={{ base: '14px', md: '15px' }}
            _hover={{ bg: 'primary.800' }}
            mt={5}
            borderRadius="12px"
          >
            {t('authPage.tabLogin')}
          </Button>
        </Fieldset.Root>
      </Stack>
    </Stack>
  );
};
